import React, { useState } from 'react';
import { Helmet } from "react-helmet-async";
import {
    ConfigProvider,
    theme,
    Table,
    Input,
    Avatar,
    Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Minus, Plus, CircleChevronDown, Funnel, Phone, Clock, MessageSquareDashed, Paperclip, Smile, Send, CircleArrowUp, UserRoundPlus, RefreshCw, Headphones, Ticket, Wallet } from 'lucide-react';

import Layout from '../../Layout'; // Giữ nguyên import layout của bạn
import { Link } from 'react-router-dom';
import { LeftOutlined } from "@ant-design/icons";
import { mixformatCurrency } from 'services/mixin';

const { TextArea } = Input;

interface reportItem {
    id: number,
    name: string,
    role: string,
    dob: string,
    phone: string,
    email: string
}

interface CommentItem {
    id: number,
    author: string,
    text: any,
    time: string,
}

// ================= DỮ LIỆU MOCK =================

export default function DebtDetail() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [rightTab, setRightTab] = useState("discussion");

    const handleSendComment = () => {
        if (!inputValue.trim()) return;
        const newComment = {
            id: Date.now(),
            author: 'Mr. Huấn',
            text: inputValue,
            time: 'Vừa xong'
        };
        setComments([...comments, newComment]);
        setInputValue('');
    };


    // Collapse States
    const [expandedBlocks, setExpandedBlocks] = useState({
        general: true,
        otherInfo: true,
        settings: true,
        relate: true,
        contact: true,
        contact2: true,
        comment: true,
    });

    const toggleBlock = (key: keyof typeof expandedBlocks) => {
        setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // SelectRow cua Seri List
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    };

    // Định nghĩa cột
    const columns: ColumnsType<reportItem> = [
        {
            title: "Họ và tên",
            dataIndex: "name",
            key: "name",
            align: "center",
        },
        {
            title: "Chức vụ",
            dataIndex: "role",
            key: "role",
            align: "center",
        },
        {
            title: "Ngày sinh",
            dataIndex: "dob",
            key: "dob",
            align: "center",
        },
        {
            title: "Điện thoại",
            dataIndex: "phone",
            key: "phone",
            align: "center",
            render: (text: any) => (
                <div style={{ paddingLeft: '4px' }} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-[11px] font-medium">
                    <Avatar size={20} className="bg-[#f96800] text-[10px]"><Phone size={15}></Phone></Avatar>
                    <span className="text-orange-600 dark:text-orange-400">{text}</span>
                </div>
            ),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            align: "center",
        },

    ];

    // ================= MOCK DATA BẢNG =================
    const reportData = [
        {
            id: 1,
            name: 'Lê Lương',
            role: '',
            dob: '',
            phone: '013884247',
            email: 'ly.dang.cap@gmail.com'
        },
    ];

    // ================= THEME & UI COMPONENTS =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
        },
        components: { Table: { headerBg: isDarkMode ? '#374151' : '#f9fafb' } },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const CardHeader = ({ title, blockKey }: { title: any, blockKey: keyof typeof expandedBlocks }) => (
        <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
            <div onClick={() => toggleBlock(blockKey)} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                {expandedBlocks[blockKey] ? <Minus size={16} /> : <Plus size={16} />}
            </div>
        </div>
    );

    return (
        <Layout>
            <Helmet>
                <title> Chi tiết báo cáo | Khothi</title>
            </Helmet>
            <div className="flex gap-2 mb-4 justify-between items-center">
                <h2 className="text-lg text-gray-900 font-semibold dark:text-white">
                    Chi tiết báo cáo
                </h2>
                {/* <button className="flex items-center gap-2 px-3 py-1.5 bg-[#3e91ab] text-white rounded text-sm font-medium hover:bg-[#357f96] transition-colors" onClick={() => navigate(`/employment-contract/add`)}>
                    <Plus size={16} /> Thêm mới
                </button> */}
            </div>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* --- HEADER ACTIONS --- */}
                    <div className="border-b dark:border-gray-700 px-4 md:px-6 py-3 flex flex-wrap justify-between items-center gap-4 sticky top-[64px] bg-white dark:bg-[#1f1f1f] z-10 shadow-sm">
                        <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                            <div className="flex sm:items-center gap-4 text-sm items-start">
                                <Link to="/debt" className="flex items-center gap-2">
                                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                                </Link>
                                <span className="font-medium text-[#3e91ab] border-b-2 border-[#3e91ab] pb-1 cursor-pointer">Thông tin chung</span>
                            </div>
                        </div>

                        <div className='overflow-x-auto no-scrollbar'>
                            <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><UserRoundPlus size={14} /> Phụ trách</button>
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><RefreshCw size={14} /> Trạng thái</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><Headphones size={14} /> Chăm sóc</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><Ticket size={14} /> Giao dịch</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><Wallet size={14} /> Công nợ</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><CircleChevronDown size={20} /></button>
                            </div>
                        </div>
                    </div>

                    {/* --- MAIN LAYOUT --- */}
                    <div className="mx-auto mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">

                        {/* CỘT TRÁI CÓ TABS CHÍNH */}
                        <div className="min-w-0 overflow-hidden">
                            <div className="flex flex-col gap-6 pt-0">

                                {/* 1. THÔNG TIN CHUNG */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin chung" blockKey="general" />
                                    {expandedBlocks.general && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 text-[13px] mt-4">

                                            {/* Cột 1 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">Mã KH</p><p className="font-medium text-gray-800 dark:text-gray-200">KH_UY.082026.537</p></div>
                                                <div><p className="text-gray-400 mb-1">Tên khách hàng</p><p className="text-gray-800 dark:text-gray-200">Công Ty Cổ Phần Kinh Doanh Và Quản Lý Bất Động Sản Vinhomes</p></div>
                                                <div><p className="text-gray-400 mb-1">Ngày thành lập</p><p className="text-gray-800 dark:text-gray-200">--</p></div>
                                                <div><p className="text-gray-400 mb-1">Lĩnh vực</p><p className="text-gray-800 dark:text-gray-200">--</p></div>

                                                <div>
                                                    <p className="text-gray-400 mb-1">Người phụ trách</p>
                                                    <div style={{ paddingLeft: '4px' }} className="inline-flex items-center  gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                        <Avatar size={20} className="bg-gray-500 text-[10px]">R</Avatar>
                                                        <span className="text-000000e0">Nguyễn Quang Huy RECO</span>
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Chăm sóc</p><p className="text-gray-800 dark:text-gray-200">--</p></div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Điện thoại</p>
                                                    <div style={{ paddingLeft: '4px' }} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-[11px] font-medium">
                                                        <Avatar size={20} className="bg-[#f96800] text-[10px]"><Phone size={15}></Phone></Avatar>
                                                        <span className="text-orange-600 dark:text-orange-400">0906336162</span>
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Email</p><p className="text-gray-800 dark:text-gray-200">Mr.dung0208@gmail.com</p></div>
                                                <div><p className="text-gray-400 mb-1">Nhà cung cấp</p><p className="text-gray-800 dark:text-gray-200">--</p></div>


                                            </div>

                                            {/* Cột 2 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">Loại khách hàng</p><p className="font-medium text-gray-800 dark:text-gray-200">Tổ chức</p></div>
                                                <div><p className="text-gray-400 mb-1">Mã số thuế/ĐKKD</p><p className="font-medium text-gray-800 dark:text-gray-200">--</p></div>
                                                <div><p className="text-gray-400 mb-1">Nhóm khách hàng</p><p className="font-medium text-gray-800 dark:text-gray-200">--</p></div>
                                                <div><p className="text-gray-400 mb-1">Nguồn khách hàng</p><p className="font-medium text-gray-800 dark:text-gray-200">Facebook</p></div>
                                                <div><p className="text-gray-400 mb-1">Quy mô tổ chức</p><p className="font-medium text-gray-800 dark:text-gray-200">5 - 10 nhân viên</p></div>
                                                <div><p className="text-gray-400 mb-1">Liên hệ  </p><p className="font-medium text-orange-600 dark:text-orange-400">Công Ty Cổ Phần Kinh Doanh Và Quản Lý Bất Động Sản Vinhomes</p></div>
                                                <div><p className="text-gray-400 mb-1">Triển khai</p><p className="font-medium text-gray-800 dark:text-gray-200">--</p></div>
                                                <div><p className="text-gray-400 mb-1">Tài khoản ngân hàng</p><p className="font-medium text-gray-800 dark:text-gray-200">--</p></div>
                                                <div><p className="text-gray-400 mb-1">Mô tả</p><p className="font-medium text-gray-800 dark:text-gray-200">--</p></div>



                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 2. DANH SÁCH SERI */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin khác" blockKey="otherInfo" />
                                    {expandedBlocks.otherInfo && (

                                        <div className='text-[13px]'><p className="text-gray-400 mb-1">Địa chỉ</p><p className="text-gray-800 dark:text-gray-200">Hà Nội</p></div>

                                    )}
                                </div>

                                {/* COT 3 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Trường tùy chỉnh" blockKey="settings" />
                                    {expandedBlocks.settings && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 text-[13px] mt-4">

                                            {/* Cột 1 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">têst</p><p className="text-gray-800 dark:text-gray-200">--</p></div>
                                            </div>

                                            {/* Cột 2 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">test</p><p className="font-medium text-gray-800 dark:text-gray-200">--</p></div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* COT 4 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Liên quan" blockKey="relate" />
                                    {expandedBlocks.relate && (
                                        <div className="animate-in fade-in duration-300 gap-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Link to={'/debt-contract/detail'} className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            [RECO] Dự án thiết kế kết cấu toà nhà thương mại dịch vụ ABC
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Nguyễn Quang Huy RECO</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Dự án</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Người thực hiện</div>
                                                    <div className="text-slate-400 dark:text-slate-500">--</div>

                                                    {/* Row 3 */}
                                                    <div className="text-slate-500 dark:text-slate-400 flex items-center">Trạng thái</div>
                                                    <div>
                                                        <span className="inline-block px-2.5 py-0.5 text-[12px] text-[#2F80ED] bg-[#E8F1FD] dark:bg-blue-900/40 dark:text-blue-300 rounded-md font-normal">
                                                            Đang thực hiện
                                                        </span>
                                                    </div>

                                                    {/* Row 4 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Kết thúc thực tế</div>
                                                    <div className="text-slate-400 dark:text-slate-500">--</div>
                                                </div>
                                            </Link>

                                            <Link to={'/debt-contract/detail'} className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            Hợp đồng
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Nguyễn Quang Huy RECO</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Hợp đồng bán</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Thành tiền</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">{mixformatCurrency(0, true)}</div>

                                                </div>
                                            </Link>
                                        </div>
                                    )}
                                </div>

                                {/* COT 5 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin người liên hệ" blockKey="contact" />
                                    {expandedBlocks.contact && (
                                        <div className="animate-in fade-in duration-300">
                                            {/* Ant Design Table */}
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Table: {
                                                            headerBg: "#f8f9fa",
                                                            headerColor: "#6b7280",
                                                            cellPaddingBlock: 12,
                                                            rowHoverBg: "#eff6ff",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Table
                                                    rowSelection={rowSelection}
                                                    columns={columns}
                                                    dataSource={reportData}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                />
                                            </ConfigProvider>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* CỘT PHẢI (THẢO LUẬN / LỊCH SỬ) - Giữ nguyên logic cũ */}
                        <div className="min-w-0 flex flex-col gap-4 h-full">

                            <div className="min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <CardHeader title="Thông tin người liên hệ" blockKey="contact" />
                                {expandedBlocks.contact && (
                                    <div className="animate-in fade-in duration-300">
                                        {/* Ant Design Table */}
                                        <ConfigProvider
                                            theme={{
                                                components: {
                                                    Table: {
                                                        headerBg: "#f8f9fa",
                                                        headerColor: "#6b7280",
                                                        cellPaddingBlock: 12,
                                                        rowHoverBg: "#eff6ff",
                                                    },
                                                },
                                            }}
                                        >
                                            <Table
                                                rowSelection={rowSelection}
                                                columns={columns}
                                                dataSource={reportData}
                                                pagination={false}
                                                scroll={{ x: "max-content" }}
                                            />
                                        </ConfigProvider>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
                                    <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                                        <button
                                            type="button"
                                            onClick={() => setRightTab("discussion")}
                                            className={`font-medium pb-1 border-b-2 transition-colors ${rightTab === "discussion"
                                                ? "text-[#3e91ab] border-[#3e91ab]"
                                                : "text-gray-500 dark:text-gray-400 border-transparent"
                                                }`}
                                        >
                                            Thảo luận
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setRightTab("attachment")}
                                            className={`font-medium pb-1 border-b-2 transition-colors ${rightTab === "attachment"
                                                ? "text-[#3e91ab] border-[#3e91ab]"
                                                : "text-gray-500 dark:text-gray-400 border-transparent"
                                                }`}
                                        >
                                            Đính kèm
                                        </button>
                                    </div>
                                    <div onClick={() => toggleBlock('comment')} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                                        {expandedBlocks['comment'] ? <Minus size={16} /> : <Plus size={16} />}
                                    </div>
                                </div>
                                {expandedBlocks.comment && (
                                    rightTab === 'discussion' ? (
                                        <div className="flex flex-col mt-2 relative">

                                            {/* Khu vực hiển thị bình luận */}
                                            <div className="flex-1 overflow-y-auto mb-4 pr-2">
                                                {comments.length === 0 ? (
                                                    <div className="flex flex-col items-center justify-center text-gray-400 opacity-60">
                                                        <MessageSquareDashed size={48} className="mb-3" strokeWidth={1} />
                                                        <p className="text-sm">Không có thảo luận nào</p>
                                                    </div>
                                                    // EMPTY STATE NẾU KHÔNG CÓ BÌNH LUẬN
                                                ) : (
                                                    // NẾU CÓ BÌNH LUẬN
                                                    comments.map(comment => (
                                                        <div key={comment.id} className="flex gap-3 mb-6">
                                                            <Avatar style={{ backgroundColor: '#84cc16' }}>{comment.author.charAt(0)}</Avatar>
                                                            <div className="flex-1">
                                                                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{comment.author}</span>
                                                                <div className="bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg rounded-tl-none mt-1 text-sm text-gray-800 w-fit">
                                                                    {comment.text}
                                                                </div>
                                                                <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500">
                                                                    <span>{comment.time}</span>
                                                                    <span className="cursor-pointer hover:text-[#3e91ab]">Thích</span>
                                                                    <span className="cursor-pointer hover:text-[#3e91ab]">Trả lời</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {/* Ô Input nhập liệu */}
                                            <div className="flex justify-between border border-gray-200 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700/30 shrink-0 mt-auto">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <CircleArrowUp className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" size={16} />
                                                    <Input
                                                        bordered={false}
                                                        placeholder="Viết thảo luận..."
                                                        className="flex-1 text-sm"
                                                        value={inputValue}
                                                        onChange={(e) => setInputValue(e.target.value)}
                                                        onPressEnter={handleSendComment}
                                                    />
                                                </div>
                                                <div className="flex gap-3 items-center text-gray-400 pl-8 pr-2">
                                                    <Paperclip size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                    <Smile size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                    <Send size={16} className="cursor-pointer text-[#3e91ab]" />
                                                </div>
                                            </div>

                                        </div>
                                    ) : (
                                        <span className="text-gray-500 dark:text-gray-400 text-sm">Không tìm thấy kết quả nào!!</span>
                                    )
                                )}

                            </div>
                        </div>
                    </div>

                </div>
            </ConfigProvider>
        </Layout>
    );
}